import React, { useState, useRef, useEffect, useCallback } from "react";
import { View, TouchableOpacity } from "react-native";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AudioWaveform from "../AudioWaveform";

const MAX_RECORDING_DURATION = 40;
const MIN_RECORDING_DURATION = 10;
const PASSAGE_TEXT = `"The rapid advancement of artificial intelligence has fundamentally transformed how we interact with technology in our daily lives. Voice cloning represents a significant breakthrough in this field, enabling highly personalized audio experiences that were once thought impossible. This recording will help create your unique voice signature, capturing the subtle nuances that make your voice distinctive. Please speak naturally and clearly throughout this passage, maintaining a comfortable pace as you would in normal conversation. Your voice contains distinctive patterns, tones, and characteristics that define who you are, and this sophisticated technology captures those essential qualities with remarkable precision."`;

const VoiceCloningModal = ({ visible, onClose, onSave }) => {
  const { currentTheme } = AppTheme.useTheme();
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();

  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [audioStream, setAudioStream] = useState(null);
  const [hasRecorded, setHasRecorded] = useState(false);

  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioElementRef = useRef(null);

  useEffect(() => {
    if (visible) {
      loadAudioDevices();
    } else {
      resetState();
    }
  }, [visible]);

  useEffect(() => {
    return () => {
      cleanupResources();
    };
  }, []);

  const resetState = () => {
    setIsRecording(false);
    setRecordingTime(0);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setIsPlaying(false);
    setPlaybackProgress(0);
    setAudioStream(null);
    setHasRecorded(false);
    chunksRef.current = [];
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current = null;
    }
  };

  const cleanupResources = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (audioElementRef.current) {
      audioElementRef.current.pause();
    }
  };

  const loadAudioDevices = async () => {
    setIsLoadingDevices(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      stream.getTracks().forEach((track) => track.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter((d) => d.kind === "audioinput");

      setAvailableDevices(audioInputs);

      if (audioInputs.length > 0 && !selectedDeviceId) {
        setSelectedDeviceId(audioInputs[0].deviceId);
      }
    } catch (error) {
      console.error("Error loading audio devices:", error);
      AppToasty.show(t("toasts.microphoneAccessFailed"), {
        type: "danger",
      });
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const startRecording = async () => {
    try {
      setRecordedBlob(null);
      setRecordedUrl(null);
      setHasRecorded(false);
      chunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
        },
      });

      mediaStreamRef.current = stream;
      setAudioStream(stream);

      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/ogg")
        ? "audio/ogg"
        : "audio/mp4";

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setRecordedBlob(blob);
        setRecordedUrl(url);
        setHasRecorded(true);
        setAudioStream(null);
        setIsRecording(false);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= MAX_RECORDING_DURATION) {
            stopRecording();
            return MAX_RECORDING_DURATION;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      AppToasty.show(
        "Failed to start recording. Please check microphone access.",
        {
          type: "danger",
        },
      );
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setAudioStream(null);
  };

  const playRecording = () => {
    if (!recordedUrl) return;

    if (!audioElementRef.current) {
      audioElementRef.current = new Audio(recordedUrl);
      audioElementRef.current.onended = () => {
        setIsPlaying(false);
        setPlaybackProgress(0);
      };
      audioElementRef.current.ontimeupdate = () => {
        if (audioElementRef.current) {
          const progress =
            (audioElementRef.current.currentTime /
              audioElementRef.current.duration) *
            100;
          setPlaybackProgress(progress);
        }
      };
    }

    audioElementRef.current.play();
    setIsPlaying(true);
  };

  const pausePlayback = () => {
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleSave = () => {
    if (!recordedBlob) {
      AppToasty.show(t("toasts.recordVoiceFirst"), { type: "warning" });
      return;
    }

    if (recordingTime < MIN_RECORDING_DURATION) {
      AppToasty.show(
        `Recording must be at least ${MIN_RECORDING_DURATION} seconds for good quality.`,
        {
          type: "warning",
        },
      );
      return;
    }

    cleanupResources();
    onSave(recordedBlob, selectedDeviceId);
  };

  const handleReRecord = () => {
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current = null;
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setIsPlaying(false);
    setPlaybackProgress(0);
    setHasRecorded(false);
    chunksRef.current = [];
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (!visible) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        backgroundColor: "rgba(0,0,0,0.5)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor:
            currentTheme?.theme?.lightGrey || currentTheme?.theme?.white,
          padding: "24px",
          borderRadius: "16px",
          maxWidth: "480px",
          width: "90%",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          border: `1px solid ${currentTheme?.theme?.border_color}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginBottom: 16,
            gap: 10,
          }}
        >
          <AppIcon
            icon={AppAssets?.icons?.microphone}
            size={24}
            color={currentTheme?.theme?.primary}
          />
          <AppText
            preventTranslation
            style={{
              fontSize: currentTheme?.theme?.medium_font_size,
              fontWeight: "600",
            }}
            color={currentTheme?.theme?.font_color}
          >
            Clone Your Voice
          </AppText>
        </View>

        <AppText
          preventTranslation
          style={{
            fontSize: currentTheme?.theme?.small_font_size,
            lineHeight: 18,
            marginBottom: 16,
          }}
          color={currentTheme?.theme?.darkGrey}
        >
          Record a voice sample to create your personalized voice clone. Select
          your microphone and read the passage below naturally.
        </AppText>

        <View style={{ marginBottom: 16 }}>
          <AppText
            preventTranslation
            style={{
              fontSize: currentTheme?.theme?.small_font_size,
              fontWeight: "500",
              marginBottom: 8,
            }}
            color={currentTheme?.theme?.font_color}
          >
            🎤 Microphone (Input)
          </AppText>
          {isLoadingDevices ? (
            <AppText
              preventTranslation
              style={{ fontSize: currentTheme?.theme?.small_font_size }}
              color={currentTheme?.theme?.darkGrey}
            >
              Loading devices...
            </AppText>
          ) : availableDevices.length === 0 ? (
            <AppText
              preventTranslation
              style={{ fontSize: currentTheme?.theme?.small_font_size }}
              color={currentTheme?.theme?.red}
            >
              No microphones found. Please connect a microphone and try again.
            </AppText>
          ) : (
            <select
              value={selectedDeviceId || ""}
              onChange={(e) => setSelectedDeviceId(e.target.value)}
              disabled={isRecording}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: `1px solid ${currentTheme?.theme?.border_color}`,
                backgroundColor: currentTheme?.theme?.inputBackgroundColor,
                color: currentTheme?.theme?.font_color,
                fontSize: currentTheme?.theme?.small_font_size,
                fontFamily: "normalFont",
                cursor: isRecording ? "not-allowed" : "pointer",
                outline: "none",
                opacity: isRecording ? 0.7 : 1,
              }}
            >
              {availableDevices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label ||
                    `Microphone ${device.deviceId.slice(0, 8)}...`}
                </option>
              ))}
            </select>
          )}
        </View>

        <View
          style={{
            backgroundColor: currentTheme?.theme?.grey,
            padding: 14,
            borderRadius: 10,
            marginBottom: 16,
            borderLeft: `3px solid ${currentTheme?.theme?.primary}`,
          }}
        >
          <AppText
            preventTranslation
            style={{
              fontSize: currentTheme?.theme?.small_font_size,
              fontStyle: "italic",
              lineHeight: 19,
            }}
            color={currentTheme?.theme?.font_color}
          >
            {PASSAGE_TEXT}
          </AppText>
        </View>

        <View style={{ alignItems: "center", marginBottom: 16 }}>
          <AudioWaveform
            audioStream={audioStream}
            isRecording={isRecording}
            audioElement={audioElementRef.current}
            isPlaying={isPlaying}
            color={
              isRecording || isPlaying
                ? currentTheme?.theme?.primary
                : currentTheme?.theme?.darkGrey
            }
          />

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginTop: 12,
              gap: 8,
            }}
          >
            <AppText
              preventTranslation
              style={{
                fontSize: currentTheme?.theme?.small_font_size,
                fontWeight: "600",
              }}
              color={
                recordingTime >= MAX_RECORDING_DURATION
                  ? currentTheme?.theme?.red
                  : recordingTime >= MIN_RECORDING_DURATION
                  ? currentTheme?.theme?.green
                  : currentTheme?.theme?.primary
              }
            >
              {formatTime(recordingTime)} / {formatTime(MAX_RECORDING_DURATION)}
            </AppText>
            {recordingTime >= MIN_RECORDING_DURATION &&
              recordingTime < MAX_RECORDING_DURATION && (
                <AppText
                  preventTranslation
                  style={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                  }}
                  color={currentTheme?.theme?.green}
                >
                  ✓ Min. duration reached
                </AppText>
              )}
          </View>
        </View>

        {!hasRecorded ? (
          <View style={{ alignItems: "center", marginBottom: 16 }}>
            <TouchableOpacity
              onPress={isRecording ? stopRecording : startRecording}
              disabled={availableDevices.length === 0}
              style={{
                backgroundColor: isRecording
                  ? currentTheme?.theme?.red
                  : currentTheme?.theme?.primary,
                paddingHorizontal: 28,
                paddingVertical: 12,
                borderRadius: 24,
                opacity: availableDevices.length === 0 ? 0.5 : 1,
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
              }}
            >
              <AppIcon
                icon={
                  isRecording
                    ? AppAssets?.icons?.stopGeneration
                    : AppAssets?.icons?.microphone
                }
                size={18}
                color={currentTheme?.theme?.white}
              />
              <AppText
                preventTranslation
                style={{
                  color: currentTheme?.theme?.white,
                  fontSize: currentTheme?.theme?.small_font_size,
                  fontWeight: "600",
                }}
              >
                {isRecording ? "Stop Recording" : "Start Recording"}
              </AppText>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={{ marginBottom: 16 }}>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 20,
                marginBottom: 12,
              }}
            >
              <AppIcon
                icon={AppAssets?.icons?.executeMode}
                size={28}
                color={
                  isPlaying
                    ? currentTheme?.theme?.orange
                    : currentTheme?.theme?.primary
                }
                isClickable
                onPress={isPlaying ? pausePlayback : playRecording}
              />

              <AppIcon
                icon={AppAssets?.icons?.record}
                size={28}
                color={currentTheme?.theme?.orange}
                isClickable
                onPress={handleReRecord}
              />
            </View>

            {isPlaying && (
              <View
                style={{
                  height: 4,
                  backgroundColor: currentTheme?.theme?.grey,
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <View
                  style={{
                    height: "100%",
                    width: `${playbackProgress}%`,
                    backgroundColor: currentTheme?.theme?.primary,
                    transition: "width 0.1s linear",
                  }}
                />
              </View>
            )}
          </View>
        )}

        <View
          style={{
            flexDirection: "row",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 8,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: `1px solid ${currentTheme?.theme?.border_color}`,
              backgroundColor: "transparent",
              color: currentTheme?.theme?.darkGrey,
              fontSize: currentTheme?.theme?.small_font_size,
              cursor: "pointer",
              fontFamily: "normalFont",
              transition: "all 0.2s",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!hasRecorded || recordingTime < MIN_RECORDING_DURATION}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              backgroundColor:
                hasRecorded && recordingTime >= MIN_RECORDING_DURATION
                  ? currentTheme?.theme?.primary
                  : currentTheme?.theme?.grey,
              color:
                hasRecorded && recordingTime >= MIN_RECORDING_DURATION
                  ? currentTheme?.theme?.white
                  : currentTheme?.theme?.darkGrey,
              fontSize: currentTheme?.theme?.small_font_size,
              cursor:
                hasRecorded && recordingTime >= MIN_RECORDING_DURATION
                  ? "pointer"
                  : "not-allowed",
              fontFamily: "normalFont",
              transition: "all 0.2s",
              opacity:
                hasRecorded && recordingTime >= MIN_RECORDING_DURATION
                  ? 1
                  : 0.6,
            }}
          >
            Save Voice Clone
          </button>
        </View>
      </div>
    </div>,
    document.body,
  );
};

export default VoiceCloningModal;
